import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { api } from "./api";

interface LogoutOptions {
	logoutParams: {
		returnTo: string;
	};
	openUrl?: (url: string) => Promise<void>;
}

// Module-level flag to prevent concurrent logout calls
// This prevents the login loop bug where multiple API failures trigger multiple logouts
let isLoggingOut = false;

/**
 * Reset the logout state flag
 * Exported for testing/edge cases where manual reset is needed
 */
export function resetLogoutState(): void {
	isLoggingOut = false;
}

/**
 * Check if a logout is currently in progress
 */
export function isLogoutInProgress(): boolean {
	return isLoggingOut;
}

/**
 * Clear user status cache from Redis before logout
 */
async function clearUserStatusCache(): Promise<void> {
	try {
		console.log("🧹 Clearing user status cache...");
		await api.post("/api/user/clear-status-cache");
		console.log("✅ User status cache cleared");
	} catch (error) {
		console.warn("⚠️ Failed to clear user status cache:", error);
		// Don't block logout if cache clearing fails
	}
}

/**
 * Unified logout function that handles both web and mobile platforms
 * Based on the MVP implementation that worked properly
 *
 * Includes deduplication to prevent multiple concurrent logout calls
 * which can cause login loops when multiple API calls fail simultaneously.
 */
export async function performLogout(
	logout: (options: LogoutOptions) => Promise<void>,
): Promise<void> {
	// Prevent multiple simultaneous logouts (fixes login loop bug)
	if (isLoggingOut) {
		console.log("⚠️ Logout already in progress, skipping duplicate call");
		return;
	}

	isLoggingOut = true;
	const isNative = Capacitor.isNativePlatform();

	console.log(`🚪 Performing logout (${isNative ? "mobile" : "web"})`);

	try {
		// Clear user status cache before logout
		await clearUserStatusCache();

		if (isNative) {
			// Mobile: Use Capacitor Browser plugin for logout following Auth0 guide
			// This should reflect the URL added to "Allowed Logout URLs" in Auth0 dashboard
			const logoutUri = `co.eraborn.app://${import.meta.env.VITE_AUTH0_DOMAIN}/capacitor/co.eraborn.app/callback`;

			console.log(
				"🚪 Mobile logout: Using Capacitor browser with deep link:",
				logoutUri,
			);

			// Clear Auth0 cache from localStorage before logout
			// This ensures no stale tokens are reused after logout completes
			console.log("🧹 Clearing Auth0 localStorage cache on mobile...");
			const keysToRemove: string[] = [];
			for (let i = 0; i < localStorage.length; i++) {
				const key = localStorage.key(i);
				// Clear all Auth0 SDK keys (they start with @@auth0spajs@@)
				if (key?.startsWith("@@auth0spajs@@")) {
					keysToRemove.push(key);
				}
			}
			for (const key of keysToRemove) {
				localStorage.removeItem(key);
			}
			console.log(`✅ Cleared ${keysToRemove.length} Auth0 cache keys`);

			await logout({
				logoutParams: {
					returnTo: logoutUri,
				},
				async openUrl(url: string) {
					// Redirect using Capacitor's Browser plugin
					await Browser.open({
						url,
						windowName: "_self",
					});
				},
			});
		} else {
			// Web: Use Auth0 SDK logout with proper redirect
			// This avoids using window.location.href which breaks Capacitor best practices
			console.log("🚪 Web logout: Using Auth0 SDK logout");

			// Clear all local storage and cookies first
			localStorage.clear();
			sessionStorage.clear();

			// Use Auth0 SDK's logout which handles the redirect properly
			// The SDK will redirect to returnTo URL after logout completes on Auth0's server
			await logout({
				logoutParams: {
					returnTo: `${window.location.origin}/login`,
				},
			});
		}
	} finally {
		// Reset the flag after a delay to allow page navigation to complete
		// The 3-second delay prevents permanent lockout if something goes wrong
		setTimeout(() => {
			isLoggingOut = false;
		}, 3000);
	}
}
